import { PortableText } from "@portabletext/react";
import Image from "gatsby-plugin-sanity-image";
import React from "react";
import { demoteH1Components } from "../../utils/portableTextComponents";

const TeamSection = ({ data }) => {
  return (
    <section className="bg-white py-16 lg:py-24">
      <div className="container mx-auto px-4 max-w-6xl">
        {/* Header Section */}
        <div className="mb-16 lg:mb-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-end">
            {/* Left Column - Title */}
            <div className="space-y-4">
              {data.caption && (
                <span className="inline-block text-logo1 text-sm font-medium uppercase tracking-wider">
                  {data.caption}
                </span>
              )}
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-black leading-tight">
                {data.title}
              </h2>
            </div>

            {/* Right Column - Description */}
            {data._rawDescription && (
              <div className="text-lg text-gray-600 leading-relaxed">
                <PortableText
                  value={data._rawDescription}
                  components={demoteH1Components}
                />
              </div>
            )}
          </div>
        </div>

        {/* Team Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {data.team?.map((member, index) => (
            <div
              key={index}
              className="group bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-xl transition-all duration-300 overflow-hidden transform hover:-translate-y-2"
            >
              {/* Member Image */}
              <div className="relative overflow-hidden">
                <Image
                  asset={member.profileImage.asset}
                  crop={member.profileImage.crop}
                  hotspot={member.profileImage.hotspot}
                  alt={member.name}
                  className="w-full h-80 object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
                {/* Overlay on hover */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </div>

              {/* Member Info */}
              <div className="p-6 space-y-3">
                {/* Designation Badge */}
                {member.designation && (
                  <span className="inline-block px-3 py-1 bg-logo1 text-black text-xs font-medium uppercase tracking-wider rounded-full">
                    {member.designation}
                  </span>
                )}

                {/* Name */}
                <h3 className="text-xl font-bold text-black group-hover:text-gray-800 transition-colors duration-200">
                  {member.name}
                </h3>

                {/* Expertise */}
                {member.expertise && (
                  <p className="text-gray-600 font-medium">
                    {member.expertise}
                  </p>
                )}

                {/* Bio if available */}
                {member.bio && (
                  <p className="text-gray-500 text-sm leading-relaxed line-clamp-3">
                    {member.bio}
                  </p>
                )}

                {/* Social Links or Contact if available */}
                {(member.email || member.linkedin || member.twitter) && (
                  <div className="flex items-center gap-3 pt-2">
                    {member.email && (
                      <a
                        href={`mailto:${member.email}`}
                        className="text-gray-400 hover:text-logo1 transition-colors duration-200"
                        title="Email"
                      >
                        <svg
                          className="w-5 h-5"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                          <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                        </svg>
                      </a>
                    )}
                    {member.linkedin && (
                      <a
                        href={member.linkedin}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-gray-400 hover:text-logo1 transition-colors duration-200"
                        title="LinkedIn"
                      >
                        <svg
                          className="w-5 h-5"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M16.338 16.338H13.67V12.16c0-.995-.017-2.277-1.387-2.277-1.39 0-1.601 1.086-1.601 2.207v4.248H8.014v-8.59h2.559v1.174h.037c.356-.675 1.227-1.387 2.526-1.387 2.703 0 3.203 1.778 3.203 4.092v4.711zM5.005 6.575a1.548 1.548 0 11-.003-3.096 1.548 1.548 0 01.003 3.096zm-1.337 9.763H6.34v-8.59H3.667v8.59zM17.668 1H2.328C1.595 1 1 1.581 1 2.298v15.403C1 18.418 1.595 19 2.328 19h15.34c.734 0 1.332-.582 1.332-1.299V2.298C19 1.581 18.402 1 17.668 1z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </a>
                    )}
                    {member.twitter && (
                      <a
                        href={member.twitter}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-gray-400 hover:text-logo1 transition-colors duration-200"
                        title="Twitter"
                      >
                        <svg
                          className="w-5 h-5"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path d="M6.29 18.251c7.547 0 11.675-6.253 11.675-11.675 0-.178 0-.355-.012-.53A8.348 8.348 0 0020 3.92a8.19 8.19 0 01-2.357.646 4.118 4.118 0 001.804-2.27 8.224 8.224 0 01-2.605.996 4.107 4.107 0 00-6.993 3.743 11.65 11.65 0 01-8.457-4.287 4.106 4.106 0 001.27 5.477A4.073 4.073 0 01.8 7.713v.052a4.105 4.105 0 003.292 4.022 4.095 4.095 0 01-1.853.07 4.108 4.108 0 003.834 2.85A8.233 8.233 0 010 16.407a11.616 11.616 0 006.29 1.84" />
                        </svg>
                      </a>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default TeamSection;
